import type { JumpEntry, ProjectState } from "@minga/core"
import { isChainHead, isOpenProposal, MARK_CLOSE, MARK_OPEN, versionsOf } from "@minga/core"
import type {
  DecidedHeader,
  HistoryHit,
  HistoryKind,
  HistoryWho,
  HitState,
  SearchGroup,
  SearchHit,
  SearchKind,
} from "@minga/protocol"
import { docKindOf, docUrlHost } from "@minga/protocol"
import { type ProjectSearch, validateProjectSearch } from "@/lib/project-search"

/**
 * Wave K (decision 0065, spec §10.3, §10.4): the pure rules of ⌘K and the search page, so they are unit-tested
 * without a browser. Words in the UI: "Search", "past discussions and runs", "Decisions" and the kind labels; never
 * "index", "FTS" or "embedding".
 */

// ---- words ---------------------------------------------------------------------------------------------------------

/** The kind label of a hit or a ⌘K row (mono caps). */
export const KIND_LABEL = {
  item: "ITEM",
  decision: "DECISION",
  goal: "GOAL",
  kpi: "KPI",
  output: "OUTPUT",
  brief: "BRIEF",
  comment: "COMMENT",
  chat: "CHAT",
  run: "RUN",
  card: "CARD",
  // wave DOC (decision 0068): a project document
  document: "DOC",
  // wave C (decision 0071): a memory (M-7)
  memory: "MEMORY",
} as const
export type KindLabelKey = keyof typeof KIND_LABEL

/** A section of the records results: Goals and KPIs share one. */
export interface Section {
  readonly key: string
  readonly title: string
  readonly kinds: ReadonlyArray<SearchKind>
  /** Only these hits of its kinds (documents and outputs share the `output` kind). */
  readonly match?: ((hit: SearchHit) => boolean) | undefined
}

/** Wave DOC (decision 0068): a project document's hit (an `output` whose path is its own page, `/o/O-n`). */
export const isDocumentHit = (hit: Pick<SearchHit, "kind" | "path">): boolean =>
  hit.kind === "output" && hit.path.startsWith("/o/")

/** A hit's kind label: "DOC" for a project document, else its kind's. */
export const hitKindLabel = (hit: Pick<SearchHit, "kind" | "path">): string =>
  isDocumentHit(hit) ? KIND_LABEL.document : KIND_LABEL[hit.kind]

export const SECTIONS: ReadonlyArray<Section> = [
  { key: "item", title: "Items", kinds: ["item"] },
  { key: "decision", title: "Decisions", kinds: ["decision"] },
  { key: "memory", title: "Memory", kinds: ["memory"] }, // wave C (decision 0071)
  { key: "goal", title: "Goals and KPIs", kinds: ["goal", "kpi"] },
  { key: "output", title: "Outputs", kinds: ["output"], match: (h) => !isDocumentHit(h) },
  { key: "document", title: "Documents", kinds: ["output"], match: isDocumentHit },
  { key: "brief", title: "Brief", kinds: ["brief"] },
  { key: "comment", title: "Comments", kinds: ["comment"] },
]

/** The sections of an answer, in order, with their hits (settled first, as the server ordered them) and `more`. */
export const sectionsOf = (
  groups: ReadonlyArray<SearchGroup>,
): ReadonlyArray<{
  readonly section: Section
  readonly hits: ReadonlyArray<SearchHit>
  readonly more: number
}> => {
  // a group's `more` is counted once, in the first section that shows hits of its kind
  const counted = new Set<SearchKind>()
  return SECTIONS.flatMap((section) => {
    const gs = groups.filter((g) => section.kinds.includes(g.kind))
    const hits = gs.flatMap((g) => (section.match ? g.hits.filter(section.match) : g.hits))
    if (hits.length === 0) return []
    // goals and KPIs merge: settled first, then newer (each group came ordered; the merge keeps that rule)
    const ordered =
      gs.length > 1 ? [...hits].sort((a, b) => Number(b.settled) - Number(a.settled) || b.at - a.at) : hits
    const more = gs.reduce((n, g) => (counted.has(g.kind) ? n : n + g.more), 0)
    for (const g of gs) counted.add(g.kind)
    return [{ section, hits: ordered, more }]
  })
}

/** A greyed hit's label (replaced, rejected, cancelled, expired); null for the rest. */
export const stateLabel = (state: HitState): string | null => {
  switch (state) {
    case "replaced":
      return "Replaced"
    case "rejected":
      return "Rejected"
    case "cancelled":
      return "Cancelled"
    case "expired":
      return "Expired"
    default:
      return null
  }
}

/** The greyed states (shown last in their band, in the muted tone). */
export const isGreyed = (state: HitState): boolean => stateLabel(state) !== null

/** A decided card's state in words (never snake_case). */
export const decidedStateWord = (state: string): string => {
  switch (state) {
    case "applied":
      return "Approved"
    case "answered":
      return "Answered"
    case "rejected":
      return "Rejected"
    case "expired":
      return "Expired"
    case "apply_failed":
      return "Approved, then failed to apply"
    case "adopted":
      return "Adopted"
    case "executed":
      return "Done"
    case "failed":
      return "Failed"
    default:
      return state.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  }
}

/** A hit's title without the ref it starts with ("P-3 Use Deepgram" → "Use Deepgram"): the ref shows on its own. */
export const displayTitle = (hit: Pick<SearchHit, "title" | "ref">): string =>
  hit.ref && hit.title.startsWith(`${hit.ref} `) ? hit.title.slice(hit.ref.length + 1) : hit.title

/** The id inside a hit's key ("output:art_1" → "art_1"), or null. */
export const keyId = (key: string, prefix: string): string | null =>
  key.startsWith(`${prefix}:`) ? key.slice(prefix.length + 1) : null

/**
 * A replaced output's successor (`→ v2`; its ref when it differs, `→ O-15`), from the state the browser holds. One
 * O-n per chain (decision 0065), so a successor in the same chain is named by its version.
 */
export const replacedBy = (s: Pick<ProjectState, "artifacts">, hit: SearchHit): string | null => {
  if (hit.state !== "replaced" || hit.kind !== "output") return null
  const id = keyId(hit.key, "output")
  const next = id ? s.artifacts.find((a) => a.supersedes === id && a.state !== "rejected") : undefined
  if (!next) return null
  return next.ref !== undefined && next.ref !== hit.ref ? next.ref : `v${versionsOf(s, next).length}`
}

/** Older versions folded under an output hit (decision 0065): "also in v1", "also in v1, v2"; null when none. */
export const alsoInLabel = (hit: Pick<SearchHit, "also">): string | null =>
  hit.also && hit.also.length > 0 ? `also in ${hit.also.map((v) => `v${v.version}`).join(", ")}` : null

/** A snippet's text and `« »` marks as pieces (marks become `<mark>`). */
export const markPieces = (
  snippet: string,
): ReadonlyArray<{ readonly text: string; readonly mark: boolean }> => {
  const out: Array<{ text: string; mark: boolean }> = []
  let rest = snippet
  for (;;) {
    const open = rest.indexOf(MARK_OPEN)
    if (open < 0) break
    const close = rest.indexOf(MARK_CLOSE, open + 1)
    if (close < 0) break
    if (open > 0) out.push({ text: rest.slice(0, open), mark: false })
    out.push({ text: rest.slice(open + 1, close), mark: true })
    rest = rest.slice(close + 1)
  }
  if (rest) out.push({ text: rest.replaceAll(MARK_OPEN, "").replaceAll(MARK_CLOSE, ""), mark: false })
  return out
}

/**
 * `text` with every word of `query` (≥ 2 characters) marked `« »` where a word of the text starts with it, so a
 * match in a title shows like one in a snippet. Case-insensitive; the search's stemming is approximated by prefixes.
 */
export const markQuery = (text: string, query: string): string => {
  const terms = [
    ...new Set(
      query
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter((t) => t.length >= 2),
    ),
  ]
  if (terms.length === 0) return text
  const escaped = terms
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .sort((a, b) => b.length - a.length)
  const re = new RegExp(`(^|[^\\p{L}\\p{N}])(${escaped.join("|")})([\\p{L}\\p{N}]*)`, "giu")
  return text.replace(
    re,
    (_m, pre: string, hit: string, rest: string) => `${pre}${MARK_OPEN}${hit}${rest}${MARK_CLOSE}`,
  )
}

/** A chat snippet split from its `Tools: …` tail (shown as its own muted line, not as part of the quote). */
export const splitToolsTail = (snippet: string): { readonly text: string; readonly tools: string | null } => {
  const i = snippet.lastIndexOf("Tools: ")
  if (i < 0) return { text: snippet, tools: null }
  const text = snippet.slice(0, i).trimEnd()
  return { text, tools: snippet.slice(i).trim() }
}

/**
 * Who wrote a history message, by name, from the state the browser holds: a member's display name, an agent's name;
 * workers and validators keep their run ref. The server sends ids for people and agents (names change).
 */
export const historyWhoLabel = (s: Pick<ProjectState, "members" | "agents">, who: HistoryWho): string => {
  switch (who._tag) {
    case "user":
      return (
        s.members.find((m) => m.userId === who.id)?.displayName ??
        (who.label !== who.id ? who.label : "Someone")
      )
    case "agent":
      return s.agents.find((a) => a.id === who.id)?.name ?? (who.label !== who.id ? who.label : "An agent")
    default:
      return who.label
  }
}

/**
 * The words for a history window in an empty answer: "in the last month" for the preset ranges, dates for a custom
 * one (with the year when it is not this year's).
 */
export const windowWords = (
  range: HistoryRange,
  window: { readonly since: number; readonly until: number },
  now: number,
  day: (ms: number) => string,
): string => {
  switch (range) {
    case "month":
      return "in the last month"
    case "quarter":
      return "in the last 3 months"
    case "year":
      return "in the last year"
    case "custom": {
      const year = new Date(now).getFullYear()
      const fmt = (ms: number) =>
        new Date(ms).getFullYear() === year ? day(ms) : `${day(ms)}, ${new Date(ms).getFullYear()}`
      return `between ${fmt(window.since)} and ${fmt(window.until)}`
    }
  }
}

// ---- where a hit goes ----------------------------------------------------------------------------------------------

/** Where opening a hit goes: a page of the project, or a sheet/viewer over the current page. */
export type HitTarget =
  | { readonly _tag: "item"; readonly ref: string; readonly search: ProjectSearch }
  | { readonly _tag: "goal"; readonly ref: string; readonly search: ProjectSearch }
  /** Wave DOC (decision 0068): a document's page (`/o/<ref>`, `?v=<n>` an older version). */
  | { readonly _tag: "document"; readonly ref: string; readonly v?: number }
  /** Wave C (decision 0071): a memory's page (`/m/M-7`). */
  | { readonly _tag: "memory"; readonly ref: string }
  | { readonly _tag: "tab"; readonly search: ProjectSearch }
  /** Search params added to the current page (the transcript sheet, the output viewer, the decision sheet). */
  | { readonly _tag: "over"; readonly search: ProjectSearch }

const SHEET_ONLY = (s: ProjectSearch): boolean =>
  Object.keys(s).length > 0 && Object.keys(s).every((k) => k === "run" || k === "frame")

/** A hit's app path (below `/w/<ws>/p/<project>`) as a target; unknown paths go to the Overview. */
export const targetOfPath = (path: string): HitTarget => {
  const [pathname = "", query = ""] = path.split("?", 2)
  const raw = Object.fromEntries(new URLSearchParams(query))
  const search = validateProjectSearch(raw)
  const item = /^\/i\/([^/?#]+)$/.exec(pathname)
  if (item?.[1]) return { _tag: "item", ref: decodeURIComponent(item[1]), search }
  const goal = /^\/g\/([^/?#]+)$/.exec(pathname)
  if (goal?.[1]) return { _tag: "goal", ref: decodeURIComponent(goal[1]), search }
  const memory = /^\/m\/([^/?#]+)$/.exec(pathname)
  if (memory?.[1]) return { _tag: "memory", ref: decodeURIComponent(memory[1]) }
  const doc = /^\/o\/([^/?#]+)$/.exec(pathname)
  if (doc?.[1]) {
    const v = Number(raw.v)
    return {
      _tag: "document",
      ref: decodeURIComponent(doc[1]),
      ...(Number.isInteger(v) && v >= 1 ? { v } : {}),
    }
  }
  if (SHEET_ONLY(search)) return { _tag: "over", search }
  return { _tag: "tab", search: Object.keys(search).length > 0 ? search : { view: "overview" } }
}

/**
 * Where a records hit opens. A decision opens the decision sheet (Needs you no longer lists a decided card); an output
 * the viewer over its item's page (or over the current page when it has no item); the rest by its path.
 */
export const targetOfHit = (s: Pick<ProjectState, "proposals">, hit: SearchHit): HitTarget => {
  if (hit.kind === "decision") {
    const id = keyId(hit.key, "decision")
    const open = id ? s.proposals.find((p) => p.id === id) : undefined
    if (id && !(open && isOpenProposal(open))) return { _tag: "over", search: { decision: id } }
  }
  if (hit.kind === "output") {
    const id = keyId(hit.key, "output")
    const t = targetOfPath(hit.path)
    // wave DOC: a document opens its page
    if (t._tag === "document") return t
    if (id && t._tag === "item") return { ...t, search: { ...t.search, output: id } }
    if (id) return { _tag: "over", search: { output: id } }
  }
  return targetOfPath(hit.path)
}

/** Where a history hit opens: the chat at the message, or the transcript sheet at the frame over the current page. */
export const targetOfHistoryHit = (hit: Pick<HistoryHit, "path">): HitTarget => targetOfPath(hit.path)

// ---- the history window ------------------------------------------------------------------------------------------

export type HistoryRange = "month" | "quarter" | "year" | "custom"
export const HISTORY_RANGES: ReadonlyArray<{ readonly key: HistoryRange; readonly label: string }> = [
  { key: "month", label: "Last month" },
  { key: "quarter", label: "3 months" },
  { key: "year", label: "Year" },
  { key: "custom", label: "Custom" },
]

const DAY = 86_400_000
const RANGE_DAYS: Record<Exclude<HistoryRange, "custom">, number> = { month: 30, quarter: 91, year: 365 }

/** "YYYY-MM-DD" (UTC midnight) → ms, or null. */
export const dayMs = (day: string | undefined): number | null => {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null
  const ms = Date.parse(`${day}T00:00:00Z`)
  return Number.isFinite(ms) ? ms : null
}

/** The `LookBack` window of a range: `since`/`until` in ms (custom: the given days, `until` inclusive). */
export const windowOf = (
  range: HistoryRange,
  now: number,
  custom: { readonly since?: string | undefined; readonly until?: string | undefined } = {},
): { readonly since?: number; readonly until?: number } => {
  if (range !== "custom") return { since: now - RANGE_DAYS[range] * DAY, until: now }
  const since = dayMs(custom.since)
  const until = dayMs(custom.until)
  return {
    ...(since !== null ? { since } : {}),
    ...(until !== null ? { until: until + DAY - 1 } : {}),
  }
}

// ---- the search page's params --------------------------------------------------------------------------------------

/** `/w/$ws/p/$project/search` params (spec §10.4). Defaults are omitted. */
export interface KnowledgeSearch {
  q?: string
  /** `records` (default, omitted) · `history`. */
  in?: "history"
  /** Records: the kinds of a "Show all" re-query. History: `chat`, `runs`. */
  kinds?: string
  /** History: the date window (`year` default, omitted). */
  range?: Exclude<HistoryRange, "year">
  since?: string
  until?: string
}

/** A change of the search page's params (`undefined` removes one). */
export type KnowledgeSearchPatch = { readonly [K in keyof KnowledgeSearch]?: KnowledgeSearch[K] | undefined }

const RECORD_KINDS: ReadonlyArray<SearchKind> = [
  "item",
  "goal",
  "kpi",
  "brief",
  "decision",
  "memory",
  "comment",
  "output",
]
const HISTORY_KINDS: ReadonlyArray<HistoryKind> = ["chat", "runs"]
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

export const validateKnowledgeSearch = (search: Record<string, unknown>): KnowledgeSearch => {
  const out: KnowledgeSearch = {}
  if (typeof search.q === "string" && search.q.trim()) out.q = search.q.slice(0, 200)
  const history = search.in === "history"
  if (history) out.in = "history"
  if (typeof search.kinds === "string") {
    const allowed: ReadonlyArray<string> = history ? HISTORY_KINDS : RECORD_KINDS
    const kinds = search.kinds.split(",").filter((k, i, all) => allowed.includes(k) && all.indexOf(k) === i)
    if (kinds.length > 0) out.kinds = kinds.join(",")
  }
  if (history) {
    const range = search.range
    if (range === "month" || range === "quarter" || range === "custom") out.range = range
    if (typeof search.since === "string" && DAY_RE.test(search.since)) out.since = search.since
    if (typeof search.until === "string" && DAY_RE.test(search.until)) out.until = search.until
  }
  return out
}

/** The record kinds of `kinds` (records mode), or undefined for all. */
export const recordKindsOf = (kinds: string | undefined): ReadonlyArray<SearchKind> | undefined => {
  const list = (kinds ?? "").split(",").filter((k): k is SearchKind => RECORD_KINDS.includes(k as SearchKind))
  return list.length > 0 ? list : undefined
}

/** The history kinds of `kinds` (history mode): both when absent. */
export const historyKindsOf = (kinds: string | undefined): ReadonlyArray<HistoryKind> => {
  const list = (kinds ?? "")
    .split(",")
    .filter((k): k is HistoryKind => HISTORY_KINDS.includes(k as HistoryKind))
  return list.length > 0 ? list : HISTORY_KINDS
}

// ---- ⌘K ------------------------------------------------------------------------------------------------------------

/** A ⌘K row's thing: what the browser holds (items, goals, KPIs, outputs, open cards) and the decided cards. */
export interface PaletteEntry extends JumpEntry {
  readonly kind: KindLabelKey
  readonly status: string
  /** Where Enter goes. */
  readonly target: HitTarget
}

const titleCase = (s: string): string => s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())

/** Everything ⌘K can jump to, in a stable order (items, goals, KPIs, outputs, memory, open cards, decided cards). */
export const paletteEntries = (
  s: Pick<ProjectState, "items" | "goals" | "kpis" | "artifacts" | "proposals"> & {
    readonly memory?: ProjectState["memory"] | undefined
  },
  decided: ReadonlyArray<DecidedHeader>,
): ReadonlyArray<PaletteEntry> => {
  const out: PaletteEntry[] = []
  for (const t of s.items)
    out.push({
      key: `item:${t.id}`,
      kind: "item",
      ref: t.ref,
      title: t.title,
      status: titleCase(t.status),
      target: { _tag: "item", ref: t.ref, search: {} },
    })
  for (const g of s.goals)
    out.push({
      key: `goal:${g.id}`,
      kind: "goal",
      ref: g.ref,
      title: g.title,
      status: titleCase(g.status),
      target: { _tag: "goal", ref: g.ref, search: {} },
    })
  for (const k of s.kpis) {
    const goal = s.goals.find((g) => g.kpiIds.includes(k.id))
    out.push({
      key: `kpi:${k.id}`,
      kind: "kpi",
      ref: k.ref,
      title: k.name,
      status: goal ? goal.ref : "",
      target: goal
        ? { _tag: "goal", ref: goal.ref, search: {} }
        : { _tag: "tab", search: { view: "overview" } },
    })
  }
  for (const a of s.artifacts) {
    // one row per O-n (decision 0065): the chain's head; the viewer shows its older versions
    if (!isChainHead(s, a)) continue
    if (a.doc !== undefined) {
      // wave DOC: a document opens its page; its row shows the path (support files are not listed)
      if (docKindOf(a) === "support" || !a.ref) continue
      out.push({
        key: `output:${a.id}`,
        kind: "document",
        ref: a.ref,
        title: a.title,
        status: a.doc.path ?? (a.doc.url ? (docUrlHost(a.doc.url) ?? "") : ""),
        target: { _tag: "document", ref: a.ref },
      })
      continue
    }
    const item = a.itemId ? s.items.find((t) => t.id === a.itemId) : undefined
    out.push({
      key: `output:${a.id}`,
      kind: "output",
      ref: a.ref ?? null,
      title: a.title,
      status: titleCase(a.state),
      target: item
        ? { _tag: "item", ref: item.ref, search: { output: a.id } }
        : { _tag: "over", search: { output: a.id } },
    })
  }
  // wave C (decision 0071): memory by ref and title (a forgotten one too: its page stays)
  for (const m of s.memory ?? []) {
    if (!m.ref) continue
    out.push({
      key: `memory:${m.id}`,
      kind: "memory",
      ref: m.ref,
      title: m.title,
      status: m.status === "accepted" ? titleCase(m.content._tag) : "Forgotten",
      target: { _tag: "memory", ref: m.ref },
    })
  }
  const inState = new Set<string>()
  for (const p of s.proposals) {
    inState.add(p.id)
    const open = isOpenProposal(p)
    if (!open && p.resolution === null) continue
    out.push({
      key: `card:${p.id}`,
      kind: open ? "card" : "decision",
      ref: p.ref,
      title: p.oneLine,
      status: open ? "Open" : decidedStateWord(p.state),
      target: open
        ? { _tag: "tab", search: { view: "needs", card: p.id } }
        : { _tag: "over", search: { decision: p.id } },
    })
  }
  for (const d of decided) {
    if (inState.has(d.id)) continue
    out.push({
      key: `card:${d.id}`,
      kind: "decision",
      ref: d.ref,
      title: d.oneLine,
      status: decidedStateWord(d.state),
      target: { _tag: "over", search: { decision: d.id } },
    })
  }
  return out
}

/** The two rows ⌘K always ends with. */
export type PaletteAction = { readonly _tag: "records" } | { readonly _tag: "history" }

/** The search page's params for "Search everything for q" and "Search past discussions and runs for q". */
export const searchPageSearch = (q: string, mode: "records" | "history"): KnowledgeSearch =>
  mode === "history" ? { q: q.trim(), in: "history" } : { q: q.trim() }

/** The palette's keyboard: ↑/↓ move (wrapping), Enter opens, ⌘Enter searches everything, Esc closes. */
export type PaletteKey =
  | { readonly _tag: "move"; readonly to: number }
  | { readonly _tag: "open"; readonly index: number }
  | { readonly _tag: "records" }
  | { readonly _tag: "close" }
  | null

/**
 * One key in the open palette over `count` rows (jump rows plus the two search rows): the index to select, the row to
 * open, "search everything", or close.
 */
export const paletteKey = (
  key: { readonly key: string; readonly metaKey?: boolean; readonly ctrlKey?: boolean },
  selected: number,
  count: number,
): PaletteKey => {
  if (key.key === "Escape") return { _tag: "close" }
  if (count <= 0) return null
  if (key.key === "ArrowDown") return { _tag: "move", to: (selected + 1) % count }
  if (key.key === "ArrowUp") return { _tag: "move", to: (selected - 1 + count) % count }
  if (key.key === "Enter")
    return key.metaKey || key.ctrlKey ? { _tag: "records" } : { _tag: "open", index: selected }
  return null
}

/** ⌘K / Ctrl-K, without Shift or Alt. */
export const isPaletteShortcut = (e: {
  readonly key: string
  readonly metaKey?: boolean
  readonly ctrlKey?: boolean
  readonly shiftKey?: boolean
  readonly altKey?: boolean
}): boolean =>
  (e.metaKey === true || e.ctrlKey === true) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "k"
